var _jsxFileName = "/opt/atlassian/pipelines/agent/build/src/components/auth/Login/index.tsx";
import React, { useCallback, useState } from 'react';
import { IconQuestionmark } from '@adc/parallax-icons';
import { ApiStatus } from 'Constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { usePartner } from 'Hooks';
import { Banner, Footer, Form, Header, Input, InputPassword, Link, LogoActionBar, Main, ScrollContainer, Spinner } from 'Components/utility';
import { logInfo } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
const initialValues = {
  email: '',
  password: ''
};
const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    authenticateUserAndRedirect,
    handleLoginError,
    handleLockoutError,
    handleCountryRegionError,
    handleUnexpectedError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage
  } = usePartner();
  const validationSchema = Yup.object().shape({
    email: Yup.string().email(i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail')).required(i18n.t('Login.content.signInForm.formField.email.errors.required')),
    password: Yup.string().required(i18n.t('Login.content.signInForm.formField.email.errors.required'))
  });
  const handleApiRequestError = useCallback((error, resetForm) => {
    const data = error.response?.data ? error.response.data : {
      details: i18n.t('Global.modals.errorCommunicatingServer.body')
    };
    logInfo('adc-webview:signin', {
      status: error.status,
      data
    });
    switch (error.status) {
      case ApiStatus.UNAUTHORIZED:
        {
          const code = error.response?.data.code;
          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
            return;
          }
          handleLoginError(error, resetForm);
          break;
        }
      case ApiStatus.BAD_REQUEST:
        {
          const code = error.response?.data.code;
          if (code === ApiStatus.UNSUPPORTED_COUNTRY || code === ApiStatus.WRONG_REGION) {
            handleCountryRegionError();
            return;
          }
          handleUnexpectedError();
          break;
        }
      case ApiStatus.TOO_MANY_REQUESTS:
        {
          handleLockoutError(error);
          resetForm();
          break;
        }
      default:
        handleUnexpectedError();
    }
  }, [handleLockoutError, handleLoginError, handleCountryRegionError, handleUnexpectedError, showHcpError]);
  const onSubmit = useCallback(async ({
    email,
    password
  }, {
    resetForm
  }) => {
    try {
      setIsLoading(true);
      await authenticateUserAndRedirect(email, password);
    } catch (err) {
      const error = err;
      handleApiRequestError(error, resetForm);
      setIsLoading(false);
    }
  }, [authenticateUserAndRedirect, handleApiRequestError]);
  const handleClickForgotPass = useCallback(() => {
    mediator.publish('router:navigate', '/forgot-password');
  }, []);
  const handleClickCreateAccount = useCallback(() => {
    mediator.publish('router:navigate', '/create-account');
  }, []);
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    errors,
    touched,
    values
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnBlur: true,
    initialErrors: {
      email: i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'),
      password: i18n.t('Login.content.signInForm.formField.email.errors.required')
    }
  });
  return /*#__PURE__*/React.createElement(Main, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 156,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement(LogoActionBar, {
    title: i18n.t('Login.title'),
    noActionButton: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 157,
      columnNumber: 7
    }
  }), isLoading && /*#__PURE__*/React.createElement(Spinner, {
    testID: "Auth.spinner",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 158,
      columnNumber: 21
    }
  }), /*#__PURE__*/React.createElement(ScrollContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 159,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(Header, {
    titleTestID: "Auth.signInTitle",
    subtitleTestID: "Auth.useFreestyleAccount",
    title: i18n.t('Login.title'),
    subtitle: i18n.t('Login.subtitle', {
      appName: i18n.t('Global.appNames.libreView')
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 160,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(Banner, {
    message: lockoutMessage,
    submessage: lockoutSubmessage,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 168,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(Form, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 169,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('Login.content.signInForm.formField.email.label'),
    placeholder: i18n.t('Login.content.signInForm.formField.email.placeholder'),
    label: i18n.t('Login.content.signInForm.formField.email.label'),
    errorMessage: errors.email,
    isInvalid: errors.email && touched.email ? true : false,
    value: values.email,
    testID: "email",
    onChangeText: handleChange('email'),
    onBlur: handleBlur('email'),
    inputMode: "email",
    disabled: isLocked,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 170,
      columnNumber: 11
    }
  }), /*#__PURE__*/React.createElement(InputPassword, {
    placeholder: i18n.t('Login.content.signInForm.formField.password.placeholder'),
    label: i18n.t('Login.content.signInForm.formField.password.label'),
    errorMessage: errors.password,
    testID: "password",
    onBlur: handleBlur('password'),
    onChangeText: handleChange('password'),
    disabled: isLocked,
    value: values.password,
    isInvalid: errors.password && touched.password ? true : false,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 183,
      columnNumber: 11
    }
  }), /*#__PURE__*/React.createElement(Link, {
    style: {
      marginTop: 10
    },
    testID: "Auth.forgotPassword",
    "aria-label": i18n.t('Login.content.signInForm.secondaryText'),
    IconElement: IconQuestionmark,
    onPress: handleClickForgotPass,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 194,
      columnNumber: 11
    }
  }, i18n.t('Login.content.signInForm.secondaryText')))), /*#__PURE__*/React.createElement(Footer, {
    buttonText: i18n.t('Login.title'),
    buttonTestID: "Auth.signIn",
    buttonLinkTitle: i18n.t('Login.content.footerCreateAccount.primaryText'),
    buttonLinkTitleTestID: "Auth.doNotHaveAccount",
    buttonLinkText: i18n.t('Login.content.footerCreateAccount.links.createAccount.label'),
    buttonLinkTestID: "Auth.createAccount",
    onButtonLinkClick: handleClickCreateAccount,
    onButtonSubmit: handleSubmit,
    isButtonDisabled: isLocked || !isValid,
    visible: !isLoading,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 205,
      columnNumber: 7
    }
  }));
};
export default Login;